import type { Localized } from '../types/notice'
import type { DashboardStats } from '../data/requestDashboard'
import { POTHOLE_ON_TIME } from '../data/requestDashboard'
import { fmtNum, pctChange, type AttentionTarget } from './requestDashboard'

/**
 * "Votre briefing territorial", prepared by ATNA from the dashboard figures.
 * Each card groups the requests tied to one situation and separates
 * facts, documented explanations and suggestions.
 */

export type BriefingBadge = 'important' | 'rising' | 'unblock' | 'followup' | 'anticipate' | 'share'
export type DraftKind = 'followup' | 'citizen'

export interface BriefingDraft {
  kind: DraftKind
  to: Localized
  subject: Localized
  body: Localized
}

export interface BriefingExplanation {
  /** documented: cause in the file · partial: some of it · unknown: nothing recorded. */
  status: 'documented' | 'partial' | 'unknown'
  text: Localized
  source?: Localized
  /** Overrides the default status label (e.g. "À vérifier"). */
  label?: Localized
}

export interface BriefingEngagement {
  label: Localized
  owner: Localized
  confirmedOn: Localized
  dueDate: Localized
  progress: number
  progressLabel: Localized
}

export interface BriefingItem {
  id: string
  badge: BriefingBadge
  title: Localized
  /** Facts: trend, volume, location. */
  summary: Localized
  why: Localized
  explanation?: BriefingExplanation
  engagement?: BriefingEngagement
  nextStep: Localized
  drafts: BriefingDraft[]
  link: { label: Localized; target: AttentionTarget }
}

export interface SinceLastVisit {
  lastVisit: Localized
  newIssues: { count: number; detail: Localized }
  worsened: { count: number; detail: Localized }
  resolved: { count: number; detail: Localized }
}

export interface TerritorialBriefing {
  since: SinceLastVisit
  issues: BriefingItem[]
  result: BriefingItem
}

const L = (fr: string, en: string): Localized => ({ fr, en })

const UNKNOWN = (fr: string, en: string): BriefingExplanation => ({
  status: 'unknown',
  text: L(fr, en),
})

function lightingItem(stats: DashboardStats, where: Localized, sectors: Localized): BriefingItem {
  const l =
    stats.issues.find((i) => i.label.fr === 'Éclairage public') ?? stats.issues[0]
  return {
    id: 'lighting',
    badge: 'share',
    title: L(
      'Éclairage public : les signalements en forte baisse',
      'Street lighting: reports sharply down',
    ),
    summary: L(
      `${fmtNum(l.count, 'fr')} demandes sur la période contre ${fmtNum(l.previous, 'fr')} la précédente (${pctChange(l.count, l.previous, 'fr')}), ${where.fr}. 3e mois de baisse consécutif ; ${sectors.fr}.`,
      `${fmtNum(l.count, 'en')} requests over the period versus ${fmtNum(l.previous, 'en')} the previous one (${pctChange(l.count, l.previous, 'en')}), ${where.en}. Third month of decline in a row; ${sectors.en}.`,
    ),
    why: L(
      'La baisse coïncide avec les remplacements faits avec le matériel du nouveau fournisseur. C’est un résultat concret, visible par les résidents, et un argument pour la suite du contrat.',
      'The drop matches the replacements made with the new supplier’s equipment. It’s a concrete result residents can see, and a point in favour of the rest of the contract.',
    ),
    explanation: {
      status: 'partial',
      text: L(
        'Depuis juin, les lampadaires remplacés proviennent du nouveau fournisseur. Les pannes répétées aux mêmes adresses sont moins fréquentes sur ces luminaires, mais aucune comparaison formelle n’a encore été faite.',
        'Since June, replaced streetlights come from the new supplier. Repeat failures at the same addresses are less frequent on these fixtures, but no formal comparison has been made yet.',
      ),
      source: L('Note au dossier · Éclairage public · 12 sept.', 'File note · Street lighting · Sept. 12'),
    },
    nextStep: L(
      'Demander aux Travaux publics de confirmer le lien avec le nouveau matériel, puis partager le résultat avec les citoyens.',
      'Ask Public Works to confirm the link with the new equipment, then share the result with citizens.',
    ),
    drafts: [
      {
        kind: 'followup',
        to: L('Direction des Travaux publics · Éclairage', 'Public Works department · Lighting'),
        subject: L(
          'Demande de suivi : baisse des signalements d’éclairage public',
          'Follow-up request: drop in street lighting reports',
        ),
        body: L(
          `Bonjour,\n\nLe tableau de bord des requêtes indique ${fmtNum(l.count, 'fr')} demandes liées à l’éclairage public ${where.fr} sur la période, contre ${fmtNum(l.previous, 'fr')} la période précédente (${pctChange(l.count, l.previous, 'fr')}).\n\nPourriez-vous me confirmer si cette baisse est liée au matériel du nouveau fournisseur, installé depuis juin, et me transmettre le taux de panne de ces luminaires comparé aux anciens ?\n\nMerci,`,
          `Hello,\n\nThe request dashboard shows ${fmtNum(l.count, 'en')} street lighting requests ${where.en} over the period, versus ${fmtNum(l.previous, 'en')} the previous period (${pctChange(l.count, l.previous, 'en')}).\n\nCould you confirm whether this drop is linked to the new supplier’s equipment, installed since June, and send me the failure rate of these fixtures compared with the old ones?\n\nThank you,`,
        ),
      },
      {
        kind: 'citizen',
        to: L('Équipe Communications, pour publication', 'Communications team, for publication'),
        subject: L('Moins de lampadaires en panne dans vos rues', 'Fewer broken streetlights on your streets'),
        body: L(
          `Bonjour,\n\nLes signalements de lampadaires défectueux ont baissé de ${pctChange(l.count, l.previous, 'fr').replace('−', '')} ces derniers mois. Les luminaires remplacés depuis juin tombent moins souvent en panne.\n\nMerci de continuer à nous signaler tout lampadaire éteint depuis l’application bciti+.`,
          `Hello,\n\nReports of faulty streetlights dropped by ${pctChange(l.count, l.previous, 'en').replace('−', '')} in recent months. Fixtures replaced since June fail less often.\n\nPlease keep reporting any streetlight that’s out from the bciti+ app.`,
        ),
      },
    ],
    link: { label: L('Voir le détail par service', 'View details by service'), target: 'sla-table' },
  }
}

/** The result to share: playground reopened after repairs. */
function resultItem(_stats: DashboardStats, _where: Localized, _extra: Localized): BriefingItem {
  return {
    id: 'result',
    badge: 'share',
    title: L(
      'Parc Brodeur : aire de jeux de nouveau accessible',
      'Parc Brodeur: playground open again',
    ),
    summary: L(
      'Les équipements signalés comme endommagés ont été réparés et l’aire de jeux a rouvert après inspection. Les 6 demandes associées sont résolues.',
      'The equipment reported as damaged has been repaired and the playground reopened after inspection. The 6 related requests are resolved.',
    ),
    why: L(
      'Les familles qui ont signalé le problème attendent de savoir que l’aire de jeux est de nouveau sûre. Leur confirmer la réouverture montre que leurs signalements mènent à des résultats concrets.',
      'The families who reported the problem want to know the playground is safe again. Confirming the reopening shows their reports lead to concrete results.',
    ),
    explanation: {
      status: 'documented',
      text: L(
        'Équipements réparés puis inspectés avant la réouverture. Les 6 demandes ont été fermées après l’inspection.',
        'Equipment repaired, then inspected before reopening. The 6 requests were closed after the inspection.',
      ),
      source: L('Rapport d’inspection · Parcs et espaces verts', 'Inspection report · Parks and green spaces'),
    },
    nextStep: L(
      'Informer les familles de la réouverture et remercier les citoyens pour leurs signalements.',
      'Tell families about the reopening and thank citizens for their reports.',
    ),
    drafts: [
      {
        kind: 'citizen',
        to: L('Équipe Communications · familles du secteur', 'Communications team · families in the area'),
        subject: L('L’aire de jeux a rouvert', 'The playground has reopened'),
        body: L(
          'Bonjour,\n\nBonne nouvelle : l’aire de jeux est de nouveau accessible ! Les équipements endommagés ont été réparés et inspectés avant la réouverture.\n\nMerci aux citoyens qui nous ont signalé le problème : vos 6 signalements nous ont permis d’agir rapidement.\n\nContinuez à nous signaler tout problème depuis l’application bciti+.',
          'Hello,\n\nGood news: the playground is open again! The damaged equipment was repaired and inspected before reopening.\n\nThank you to the citizens who reported the problem: your 6 reports helped us act quickly.\n\nKeep reporting any issue from the bciti+ app.',
        ),
      },
    ],
    link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
  }
}

function cityBriefing(stats: DashboardStats): TerritorialBriefing {
  return {
    since: {
      lastVisit: L('23 sept.', 'Sept. 23'),
      newIssues: {
        count: 2,
        detail: L(
          'Carrefour Alarie / Aline, compost en hausse',
          'Alarie / Aline intersection, compost rising',
        ),
      },
      worsened: {
        count: 1,
        detail: L(
          'Nids-de-poule passés sous le seuil de 80 %',
          'Potholes fell below the 80% threshold',
        ),
      },
      resolved: {
        count: stats.latePrev - stats.late,
        detail: L(
          `Retards résolus (${stats.latePrev} → ${stats.late})`,
          `Late requests resolved (${stats.latePrev} → ${stats.late})`,
        ),
      },
    },
    issues: [
      {
        id: 'school-crossing',
        badge: 'important',
        title: L(
          'Carrefour dangereux près de l’école Good Shepherd',
          'Dangerous intersection near Good Shepherd school',
        ),
        summary: L(
          '9 citoyens ont signalé le carrefour de la rue Alarie et de la rue Aline comme dangereux depuis la rentrée, contre 2 sur la même période l’an dernier. L’école primaire Good Shepherd (5770, rue Aline) est à moins de 100 m. Motifs : vitesse, arrêts non respectés, traversée difficile aux heures d’entrée et de sortie des classes. District 4, secteur A.',
          '9 citizens have reported the rue Alarie and rue Aline intersection as dangerous since the start of the school year, versus 2 over the same period last year. Good Shepherd elementary school (5770 rue Aline) is less than 100 m away. Reasons: speeding, stops not respected, hard to cross when classes start and end. District 4, sector A.',
        ),
        why: L(
          'Les signalements viennent surtout de parents d’élèves et se concentrent aux heures d’entrée et de sortie. Aucun accident n’est consigné aux dossiers, mais la sécurité des enfants près d’une école est un enjeu prioritaire pour les résidents.',
          'Reports come mostly from students’ parents and cluster around school start and end times. No accident is recorded in the files, but children’s safety near a school is a top concern for residents.',
        ),
        explanation: {
          status: 'unknown',
          label: L('À vérifier', 'To verify'),
          text: L(
            'Aucun diagnostic de circulation n’est joint aux dossiers. Une analyse permettrait de confirmer les problèmes et les mesures adaptées.',
            'No traffic assessment is attached to the files. An analysis would confirm the problems and the appropriate measures.',
          ),
        },
        nextStep: L(
          'Demander un diagnostic de sécurité du carrefour (comptage, vitesse) et évaluer des mesures pour la zone scolaire : brigadier, traverse surélevée, dos d’âne, saillies de trottoir.',
          'Ask for a safety assessment of the intersection (counts, speed) and assess measures for the school zone: crossing guard, raised crosswalk, speed bumps, curb extensions.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction du Génie · Circulation et sécurité routière', 'Engineering department · Traffic and road safety'),
            subject: L(
              'Demande de suivi : sécurité du carrefour Alarie / Aline (école Good Shepherd)',
              'Follow-up request: safety of the Alarie / Aline intersection (Good Shepherd school)',
            ),
            body: L(
              'Bonjour,\n\n9 citoyens ont signalé le carrefour de la rue Alarie et de la rue Aline comme dangereux depuis la rentrée, contre 2 l’an dernier à la même période. L’école primaire Good Shepherd se trouve à moins de 100 m, et les signalements portent sur la vitesse, les arrêts non respectés et la traversée aux heures d’entrée et de sortie des classes.\n\nPourriez-vous :\n- réaliser un diagnostic de sécurité du carrefour (comptage, vitesse pratiquée) ;\n- évaluer les mesures possibles pour la zone scolaire (brigadier, traverse surélevée, dos d’âne, saillies de trottoir) ;\n- m’indiquer un échéancier ?\n\nMerci,',
              'Hello,\n\n9 citizens have reported the rue Alarie and rue Aline intersection as dangerous since the start of the school year, versus 2 at the same time last year. Good Shepherd elementary school is less than 100 m away, and the reports mention speeding, stops not respected and crossing at school start and end times.\n\nCould you:\n- carry out a safety assessment of the intersection (counts, actual speeds);\n- assess possible measures for the school zone (crossing guard, raised crosswalk, speed bumps, curb extensions);\n- give me a timeline?\n\nThank you,',
            ),
          },
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du secteur A et parents de l’école', 'Communications team · sector A residents and school parents'),
            subject: L('Sécurité près de l’école Good Shepherd', 'Safety near Good Shepherd school'),
            body: L(
              'Bonjour,\n\nMerci aux citoyens qui ont signalé le carrefour de la rue Alarie et de la rue Aline. La Ville a demandé un diagnostic de sécurité de ce carrefour, situé près de l’école Good Shepherd, afin d’évaluer les aménagements possibles.\n\nD’ici là, nous rappelons aux automobilistes de respecter la limite de vitesse en zone scolaire et les arrêts obligatoires.\n\nStéphanie Quintal, conseillère municipale du district 4',
              'Hello,\n\nThank you to the citizens who reported the rue Alarie and rue Aline intersection. The City has requested a safety assessment of this intersection, near Good Shepherd school, to evaluate possible improvements.\n\nIn the meantime, we remind drivers to respect the school-zone speed limit and mandatory stops.\n\nStéphanie Quintal, City councillor for district 4',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'compost',
        badge: 'rising',
        title: L('Compost : les problèmes de tri augmentent', 'Compost: sorting problems are rising'),
        summary: L(
          '38 demandes liées au bac brun sur la période contre 17 la précédente (+124 %) : odeurs, bacs refusés à la collecte, vers et animaux. Le fournisseur de collecte a aussi rapporté 5 débuts d’incendie dans les camions de compost depuis le 1er septembre, dans les secteurs A, M et R.',
          '38 brown-bin requests over the period versus 17 the previous one (+124%): odours, bins refused at pickup, maggots and animals. The collection contractor also reported 5 small fires in compost trucks since September 1, in sectors A, M and R.',
        ),
        why: L(
          'Les incendies dans les camions mettent les équipes de collecte en danger et immobilisent les véhicules. Les bacs refusés génèrent à leur tour de nouvelles plaintes de résidents.',
          'Truck fires put collection crews at risk and take vehicles off the road. Refused bins in turn lead to new complaints from residents.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Incendies : cendres de foyer ou de barbecue encore chaudes déposées dans le bac brun. Bacs refusés : sacs de plastique et matières non acceptées.',
            'Fires: fireplace or barbecue ashes still hot when put in the brown bin. Refused bins: plastic bags and non-accepted materials.',
          ),
          source: L(
            'Rapports du fournisseur de collecte · 8, 15 et 22 sept.',
            'Collection contractor reports · Sept. 8, 15 and 22',
          ),
        },
        nextStep: L(
          'Lancer une campagne de sensibilisation sur le bac brun à l’échelle de la ville, avant la saison des foyers.',
          'Run a city-wide brown-bin awareness campaign before fireplace season.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · tous les résidents', 'Communications team · all residents'),
            subject: L(
              'Bac brun : les bons gestes pour un compost sans danger',
              'Brown bin: the right habits for safe composting',
            ),
            body: L(
              'Bonjour,\n\nDepuis le début du mois, des cendres encore chaudes déposées dans le bac brun ont causé 5 débuts d’incendie dans les camions de collecte. Des bacs ont aussi été refusés à cause de sacs de plastique.\n\nQuelques rappels simples :\n- Laissez refroidir les cendres au moins 7 jours dans un contenant de métal avant de les jeter.\n- N’utilisez jamais de sacs de plastique, même « compostables » : choisissez des sacs de papier.\n- Enveloppez les restes de viande dans du papier journal pour limiter les odeurs et les vers.\n\nLa liste des matières acceptées est disponible dans l’application bciti+. Merci de votre collaboration !\n\nDoreen Assaad, mairesse de Brossard',
              'Hello,\n\nSince the start of the month, ashes still hot when put in the brown bin caused 5 small fires in collection trucks. Some bins were also refused because of plastic bags.\n\nA few simple reminders:\n- Let ashes cool for at least 7 days in a metal container before throwing them out.\n- Never use plastic bags, even “compostable” ones: choose paper bags.\n- Wrap meat scraps in newspaper to limit odours and maggots.\n\nThe list of accepted materials is available in the bciti+ app. Thank you for your help!\n\nDoreen Assaad, Mayor of Brossard',
            ),
          },
          {
            kind: 'followup',
            to: L('Service de l’environnement · Matières résiduelles', 'Environment department · Waste management'),
            subject: L(
              'Demande de suivi : incidents liés au compost',
              'Follow-up request: compost incidents',
            ),
            body: L(
              'Bonjour,\n\nLes demandes liées au bac brun sont passées de 17 à 38 sur la période, et le fournisseur de collecte a rapporté 5 débuts d’incendie dans les camions depuis le 1er septembre.\n\nPourriez-vous m’indiquer si une campagne de sensibilisation est prévue, et quels outils (dépliants, autocollants pour bacs, publication dans l’application) peuvent être déployés rapidement ?\n\nMerci,',
              'Hello,\n\nBrown-bin requests went from 17 to 38 over the period, and the collection contractor reported 5 small fires in trucks since September 1.\n\nCould you tell me whether an awareness campaign is planned, and which materials (leaflets, bin stickers, an in-app post) could be rolled out quickly?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'winter',
        badge: 'anticipate',
        title: L(
          'Premières neiges : le pic de demandes arrive dans 7 semaines',
          'First snowfalls: the request peak is 7 weeks away',
        ),
        summary: L(
          'L’hiver dernier, 612 demandes liées au déneigement entre le 15 novembre et le 15 décembre, dont 41 % lors des 2 premières tempêtes. Principaux motifs : véhicules gênant le déneigement, bordures et gazon abîmés, accès bloqués par l’andain.',
          'Last winter, 612 snow-removal requests between November 15 and December 15, 41% of them during the first 2 storms. Main reasons: vehicles in the way of snow clearing, damaged curbs and lawns, driveways blocked by the windrow.',
        ),
        why: L(
          'C’est le moment de l’année où les résidents sollicitent le plus leurs élus. Une information claire avant la première neige réduit les plaintes et les remorquages.',
          'It’s the time of year when residents contact their elected officials the most. Clear information before the first snow reduces complaints and towing.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Données des requêtes de l’hiver 2025-2026 : la moitié des demandes des 2 premières tempêtes concernaient le stationnement dans la rue pendant le déneigement.',
            'Winter 2025-2026 request data: half of the requests during the first 2 storms were about on-street parking during snow clearing.',
          ),
          source: L('Requêtes bciti+ · hiver 2025-2026', 'bciti+ requests · winter 2025-2026'),
        },
        nextStep: L(
          'Demander le plan de déneigement 2026-2027 et préparer un rappel aux citoyens sur le stationnement hivernal avant le 15 novembre.',
          'Ask for the 2026-2027 snow-removal plan and prepare a reminder to citizens on winter parking before November 15.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Déneigement', 'Public Works department · Snow removal'),
            subject: L(
              'Demande de suivi : préparation de la saison de déneigement',
              'Follow-up request: preparing the snow-removal season',
            ),
            body: L(
              'Bonjour,\n\nL’hiver dernier, 612 demandes liées au déneigement ont été reçues entre le 15 novembre et le 15 décembre, dont 41 % lors des 2 premières tempêtes.\n\nPourriez-vous me transmettre :\n- le plan de déneigement 2026-2027 et les éventuels changements de parcours ;\n- les secteurs où les plaintes ont été les plus nombreuses, et les mesures prévues ?\n\nMerci,',
              'Hello,\n\nLast winter, 612 snow-removal requests were received between November 15 and December 15, 41% of them during the first 2 storms.\n\nCould you send me:\n- the 2026-2027 snow-removal plan and any route changes;\n- the sectors with the most complaints, and the measures planned?\n\nThank you,',
            ),
          },
          {
            kind: 'citizen',
            to: L('Équipe Communications · tous les résidents', 'Communications team · all residents'),
            subject: L('Préparez-vous pour l’hiver', 'Get ready for winter'),
            body: L(
              'Bonjour,\n\nL’hiver approche ! Quelques rappels pour faciliter le déneigement :\n- Respectez les règles de stationnement hivernal et activez les alertes de déneigement dans l’application bciti+.\n- Installez vos balises à au moins 60 cm de la bordure.\n- Ne déposez pas la neige de votre entrée dans la rue.\n\nMerci de votre collaboration !\n\nDoreen Assaad, mairesse de Brossard',
              'Hello,\n\nWinter is coming! A few reminders to make snow clearing easier:\n- Follow the winter parking rules and turn on snow-removal alerts in the bciti+ app.\n- Place your markers at least 60 cm from the curb.\n- Don’t push the snow from your driveway into the street.\n\nThank you for your help!\n\nDoreen Assaad, Mayor of Brossard',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      lightingItem(
        stats,
        L('de la ville', 'in the city'),
        L('baisse la plus nette dans les secteurs O, I et C', 'sharpest drop in sectors O, I and C'),
      ),
    ],
    result: resultItem(
      stats,
      L('à l’échelle de la ville', 'city-wide'),
      L(
        `Demandes en retard passées de ${stats.latePrev} à ${stats.late} en 30 jours.`,
        `Late requests down from ${stats.latePrev} to ${stats.late} in 30 days.`,
      ),
    ),
  }
}

function district1Briefing(stats: DashboardStats): TerritorialBriefing {
  const potholes = stats.issues[1]
  return {
    since: {
      lastVisit: L('25 sept.', 'Sept. 25'),
      newIssues: { count: 1, detail: L('2 lampadaires en attente d’Hydro-Québec', '2 streetlights waiting on Hydro-Québec') },
      worsened: { count: 0, detail: L('Aucune situation aggravée', 'No situation worsened') },
      resolved: { count: 1, detail: L('Dernier retard du district résolu', 'District’s last late request resolved') },
    },
    issues: [
      lightingItem(
        stats,
        L('du district', 'in the district'),
        L('baisse la plus nette dans les secteurs C et L', 'sharpest drop in sectors C and L'),
      ),
      {
        id: 'hydro',
        badge: 'unblock',
        title: L(
          'Secteur E : 2 lampadaires en attente d’Hydro-Québec',
          'Sector E: 2 streetlights waiting on Hydro-Québec',
        ),
        summary: L(
          '2 demandes de réparation ouvertes depuis 6 jours (SLA 14 j), rue de Lausanne. Encore dans les délais, mais bloquées par une intervention externe.',
          '2 repair requests open for 6 days (14-day SLA), rue de Lausanne. Still on time, but blocked by an outside intervention.',
        ),
        why: L(
          'La date dépend d’un tiers : sans date confirmée, la Ville ne peut pas garantir le délai aux résidents.',
          'The date depends on a third party: without a confirmed date, the City can’t guarantee the deadline to residents.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Défaut sur le réseau d’alimentation ; le raccordement relève d’Hydro-Québec.',
            'Fault on the power network; the connection is Hydro-Québec’s responsibility.',
          ),
          source: L('Note au dossier · Éclairage public · 26 sept.', 'File note · Street lighting · Sept. 26'),
        },
        nextStep: L(
          'Demander à la direction d’obtenir une date d’intervention auprès d’Hydro-Québec.',
          'Ask the department to get an intervention date from Hydro-Québec.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Éclairage', 'Public Works department · Lighting'),
            subject: L(
              'Demande de suivi : lampadaires en attente d’Hydro-Québec, secteur E',
              'Follow-up request: streetlights waiting on Hydro-Québec, sector E',
            ),
            body: L(
              'Bonjour,\n\nDeux demandes de réparation de lampadaires de la rue de Lausanne (secteur E) sont en attente d’une intervention d’Hydro-Québec depuis le 26 septembre.\n\nPourriez-vous relancer Hydro-Québec et me confirmer la date d’intervention prévue, afin que je puisse informer les résidents ?\n\nMerci,',
              'Hello,\n\nTwo streetlight repair requests on rue de Lausanne (sector E) have been waiting for a Hydro-Québec intervention since September 26.\n\nCould you follow up with Hydro-Québec and confirm the planned date, so I can inform residents?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'potholes-l',
        badge: 'followup',
        title: L('Secteur L : campagne de colmatage en cours', 'Sector L: pothole patching campaign under way'),
        summary: L(
          `${fmtNum(potholes.count, 'fr')} demandes de nids-de-poule dans le district (${pctChange(potholes.count, potholes.previous, 'fr')}), dont 21 dans le secteur L. À l’échelle de la ville, ce service est à ${POTHOLE_ON_TIME.current} % dans les délais, sous le seuil de ${POTHOLE_ON_TIME.threshold} %.`,
          `${fmtNum(potholes.count, 'en')} pothole requests in the district (${pctChange(potholes.count, potholes.previous, 'en')}), including 21 in sector L. City-wide, this service is at ${POTHOLE_ON_TIME.current}% on time, below the ${POTHOLE_ON_TIME.threshold}% threshold.`,
        ),
        why: L(
          'Le secteur L regroupe plus d’un tiers des demandes du district, et les Travaux publics se sont engagés sur une date.',
          'Sector L holds more than a third of the district’s requests, and Public Works has committed to a date.',
        ),
        engagement: {
          label: L('Colmatage du secteur L', 'Sector L patching'),
          owner: L('Travaux publics', 'Public Works'),
          confirmedOn: L('confirmé le 25 sept.', 'confirmed Sept. 25'),
          dueDate: L('9 oct. 2026', 'Oct. 9, 2026'),
          progress: 62,
          progressLabel: L('13 réparations sur 21', '13 of 21 repairs'),
        },
        nextStep: L(
          'Suivre l’avancement et informer les résidents du secteur L à la fin de la campagne.',
          'Track progress and inform sector L residents when the campaign ends.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du secteur L', 'Communications team · sector L residents'),
            subject: L('Réparation des nids-de-poule dans votre secteur', 'Pothole repairs in your sector'),
            body: L(
              'Bonjour,\n\nLes Travaux publics mènent une campagne de colmatage des nids-de-poule dans votre secteur : 13 des 21 réparations signalées sont déjà faites, et les autres sont prévues d’ici le 9 octobre 2026.\n\nMerci pour vos signalements.\n\nChristian Gaudette, conseiller municipal — District 1',
              'Hello,\n\nPublic Works is running a pothole patching campaign in your sector: 13 of the 21 reported repairs are already done, and the rest are planned by October 9, 2026.\n\nThank you for your reports.\n\nChristian Gaudette, city councillor — District 1',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'compost-d1',
        badge: 'rising',
        title: L('Compost : les bacs refusés augmentent', 'Compost: refused bins are rising'),
        summary: L(
          '9 demandes liées au bac brun sur la période contre 4 la précédente (+125 %), surtout dans les secteurs C et L : bacs refusés à la collecte et odeurs. Aucun incendie de camion rapporté dans le district, contre 5 dans la ville.',
          '9 brown-bin requests over the period versus 4 the previous one (+125%), mostly in sectors C and L: bins refused at pickup and odours. No truck fire reported in the district, versus 5 city-wide.',
        ),
        why: L(
          'La hausse suit la tendance de la ville, où des cendres chaudes ont causé des incendies dans les camions. Informer tôt évite que le district soit touché.',
          'The rise follows the city trend, where hot ashes caused truck fires. Informing residents early helps keep the district from being affected.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Bacs refusés : sacs de plastique et matières non acceptées.',
            'Refused bins: plastic bags and non-accepted materials.',
          ),
          source: L('Rapports du fournisseur de collecte · 15 et 22 sept.', 'Collection contractor reports · Sept. 15 and 22'),
        },
        nextStep: L(
          'Relayer la campagne de sensibilisation sur le bac brun auprès des résidents du district, avant la saison des foyers.',
          'Share the brown-bin awareness campaign with district residents before fireplace season.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du district 1', 'Communications team · district 1 residents'),
            subject: L('Bac brun : les bons gestes', 'Brown bin: the right habits'),
            body: L(
              'Bonjour,\n\nDes bacs bruns ont été refusés à la collecte ces dernières semaines dans le district, surtout à cause de sacs de plastique.\n\nQuelques rappels :\n- Utilisez des sacs de papier, jamais de plastique.\n- Laissez refroidir les cendres au moins 7 jours dans un contenant de métal.\n- Enveloppez les restes de viande dans du papier journal.\n\nMerci de votre collaboration !\n\nChristian Gaudette, conseiller municipal — District 1',
              'Hello,\n\nSome brown bins were refused at pickup in the district in recent weeks, mostly because of plastic bags.\n\nA few reminders:\n- Use paper bags, never plastic.\n- Let ashes cool for at least 7 days in a metal container.\n- Wrap meat scraps in newspaper.\n\nThank you for your help!\n\nChristian Gaudette, city councillor — District 1',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
    ],
    result: resultItem(
      stats,
      L('aux résidents du district', 'with district residents'),
      L('Aucune demande en retard à ce jour.', 'No late request as of today.'),
    ),
  }
}

function district4Briefing(stats: DashboardStats): TerritorialBriefing {
  const potholes = stats.issues[1]
  return {
    since: {
      lastVisit: L('24 sept.', 'Sept. 24'),
      newIssues: { count: 1, detail: L('Panneau en attente du ministère', 'Sign waiting on the ministry') },
      worsened: { count: 1, detail: L('Problèmes liés au compost en hausse', 'Compost-related issues rising') },
      resolved: { count: 0, detail: L('Aucun retard à résoudre', 'No late request to resolve') },
    },
    issues: [
      lightingItem(
        stats,
        L('du district', 'in the district'),
        L('surtout dans le secteur A', 'mostly in sector A'),
      ),
      {
        id: 'mtq',
        badge: 'unblock',
        title: L(
          'Boulevard Taschereau : panneau en attente du ministère',
          'Boulevard Taschereau: sign waiting on the ministry',
        ),
        summary: L(
          '1 demande de remplacement d’un panneau de limite de vitesse endommagé, ouverte depuis 5 jours, encore dans les délais. Transmise au ministère des Transports le 27 sept.',
          '1 request to replace a damaged speed limit sign, open for 5 days, still on time. Sent to the Ministère des Transports on Sept. 27.',
        ),
        why: L(
          'Un panneau de vitesse manquant touche la sécurité, et la Ville ne contrôle pas la date d’intervention.',
          'A missing speed sign affects safety, and the City doesn’t control the intervention date.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Le boulevard Taschereau (route 134) relève du ministère des Transports.',
            'Boulevard Taschereau (Route 134) is under the Ministère des Transports.',
          ),
          source: L('Note au dossier · Signalisation · 27 sept.', 'File note · Signage · Sept. 27'),
        },
        nextStep: L(
          'Demander au service de relancer le ministère et de confirmer une date.',
          'Ask the department to follow up with the ministry and confirm a date.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Signalisation', 'Public Works department · Signage'),
            subject: L(
              'Demande de suivi : panneau de vitesse, boulevard Taschereau',
              'Follow-up request: speed sign, boulevard Taschereau',
            ),
            body: L(
              'Bonjour,\n\nLa demande de remplacement d’un panneau de limite de vitesse sur le boulevard Taschereau a été transmise au ministère des Transports le 27 septembre.\n\nPourriez-vous relancer le ministère et me confirmer la date d’intervention prévue ?\n\nMerci,',
              'Hello,\n\nThe request to replace a speed limit sign on boulevard Taschereau was sent to the Ministère des Transports on September 27.\n\nCould you follow up with the ministry and confirm the planned date?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'compost',
        badge: 'rising',
        title: L(
          'Compost : les problèmes de tri augmentent',
          'Compost: sorting problems are rising',
        ),
        summary: L(
          '14 demandes liées au bac brun sur la période contre 6 la précédente (+133 %) : odeurs, bacs refusés à la collecte, vers et animaux. Le fournisseur de collecte a aussi rapporté 2 débuts d’incendie dans les camions de compost du secteur A depuis le 1er septembre.',
          '14 brown-bin requests over the period versus 6 the previous one (+133%): odours, bins refused at pickup, maggots and animals. The collection contractor also reported 2 small fires in compost trucks in sector A since September 1.',
        ),
        why: L(
          'Les incendies dans les camions mettent les équipes de collecte en danger et immobilisent les véhicules. Les bacs refusés génèrent à leur tour de nouvelles plaintes de résidents.',
          'Truck fires put collection crews at risk and take vehicles off the road. Refused bins in turn lead to new complaints from residents.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Incendies : cendres de foyer ou de barbecue encore chaudes déposées dans le bac brun. Bacs refusés : sacs de plastique et matières non acceptées.',
            'Fires: fireplace or barbecue ashes still hot when put in the brown bin. Refused bins: plastic bags and non-accepted materials.',
          ),
          source: L(
            'Rapports du fournisseur de collecte · 8 et 22 sept.',
            'Collection contractor reports · Sept. 8 and 22',
          ),
        },
        nextStep: L(
          'Lancer une campagne de sensibilisation sur le bac brun dans le district, en priorité dans le secteur A, avant la saison des foyers.',
          'Run a brown-bin awareness campaign in the district, starting with sector A, before fireplace season.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du district 4', 'Communications team · district 4 residents'),
            subject: L(
              'Bac brun : les bons gestes pour un compost sans danger',
              'Brown bin: the right habits for safe composting',
            ),
            body: L(
              'Bonjour,\n\nCes dernières semaines, des cendres encore chaudes déposées dans le bac brun ont causé 2 débuts d’incendie dans les camions de collecte de notre secteur. Des bacs ont aussi été refusés à cause de sacs de plastique.\n\nQuelques rappels simples :\n- Laissez refroidir les cendres au moins 7 jours dans un contenant de métal avant de les jeter.\n- N’utilisez jamais de sacs de plastique, même « compostables » : choisissez des sacs de papier.\n- Enveloppez les restes de viande dans du papier journal pour limiter les odeurs et les vers.\n\nLa liste des matières acceptées est disponible dans l’application bciti+. Merci de votre collaboration !\n\nStéphanie Quintal, conseillère municipale — District 4',
              'Hello,\n\nIn recent weeks, ashes still hot when put in the brown bin caused 2 small fires in collection trucks in our sector. Some bins were also refused because of plastic bags.\n\nA few simple reminders:\n- Let ashes cool for at least 7 days in a metal container before throwing them out.\n- Never use plastic bags, even “compostable” ones: choose paper bags.\n- Wrap meat scraps in newspaper to limit odours and maggots.\n\nThe list of accepted materials is available in the bciti+ app. Thank you for your help!\n\nStéphanie Quintal, city councillor — District 4',
            ),
          },
          {
            kind: 'followup',
            to: L('Service de l’environnement · Matières résiduelles', 'Environment department · Waste management'),
            subject: L(
              'Demande de suivi : incidents liés au compost, district 4',
              'Follow-up request: compost incidents, district 4',
            ),
            body: L(
              'Bonjour,\n\nLes demandes liées au bac brun sont passées de 6 à 14 dans le district 4, et le fournisseur de collecte a rapporté 2 débuts d’incendie dans les camions du secteur A.\n\nPourriez-vous m’indiquer si une campagne de sensibilisation est prévue, et si des outils existants (dépliants, autocollants pour bacs) peuvent être distribués dans le secteur A ?\n\nMerci,',
              'Hello,\n\nBrown-bin requests went from 6 to 14 in district 4, and the collection contractor reported 2 small fires in sector A trucks.\n\nCould you tell me whether an awareness campaign is planned, and whether existing materials (leaflets, bin stickers) could be distributed in sector A?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'missed-pickups',
        badge: 'followup',
        title: L('Secteur A : 3 collectes manquées à rattraper', 'Sector A: 3 missed pickups to catch up'),
        summary: L(
          '3 demandes de bac non ramassé le 29 sept., sur la même rue (rue Agathe). Toutes dans les délais (rattrapage sous 48 h ouvrables).',
          '3 missed-bin requests on Sept. 29, on the same street (rue Agathe). All on time (catch-up within 48 business hours).',
        ),
        why: L(
          'Plusieurs adresses voisines le même jour : les résidents attendent une confirmation, et le rattrapage a une date fixe.',
          'Several neighbouring addresses on the same day: residents expect confirmation, and the catch-up has a fixed date.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Camion en panne pendant la tournée ; la fin du parcours n’a pas été faite.',
            'Truck broke down during the route; the end of the run wasn’t done.',
          ),
          source: L('Rapport du fournisseur de collecte · 29 sept.', 'Collection contractor report · Sept. 29'),
        },
        engagement: {
          label: L('Rattrapage de la collecte', 'Pickup catch-up'),
          owner: L('Travaux publics', 'Public Works'),
          confirmedOn: L('confirmé le 30 sept.', 'confirmed Sept. 30'),
          dueDate: L('1er oct. 2026', 'Oct. 1, 2026'),
          progress: 0,
          progressLabel: L('Camion de remplacement planifié', 'Replacement truck scheduled'),
        },
        nextStep: L(
          'Vérifier demain que le rattrapage a eu lieu.',
          'Check tomorrow that the catch-up took place.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Collectes', 'Public Works department · Collections'),
            subject: L(
              'Demande de suivi : collectes manquées, rue Agathe',
              'Follow-up request: missed pickups, rue Agathe',
            ),
            body: L(
              'Bonjour,\n\nTrois résidents de la rue Agathe (secteur A) ont signalé un bac non ramassé le 29 septembre. Le rattrapage est annoncé pour le 1er octobre.\n\nPourriez-vous me confirmer qu’il a bien été fait ?\n\nMerci,',
              'Hello,\n\nThree residents of rue Agathe (sector A) reported a missed bin on September 29. The catch-up is announced for October 1.\n\nCould you confirm it was done?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
    ],
    result: resultItem(
      stats,
      L('aux résidents du district', 'with district residents'),
      L(
        `Aucune demande en retard, et ${pctChange(potholes.count, potholes.previous, 'fr')} de nids-de-poule signalés.`,
        `No late request, and ${pctChange(potholes.count, potholes.previous, 'en')} pothole reports.`,
      ),
    ),
  }
}

/** Briefing for the selected territory: a councillor's district or the whole city. */
export function buildBriefing(stats: DashboardStats, districtId: string | null): TerritorialBriefing {
  if (districtId === 'd1') return district1Briefing(stats)
  if (districtId === 'd4') return district4Briefing(stats)
  return cityBriefing(stats)
}
